import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  getProcedureRevisionsForPart,
  getTrainingLogForPart 
} from '../../../redux/procedureRevisionSlice';
import { getBom } from '../../../redux/partSlice';

/**
 * Custom hook for ViewDocument page data fetching
 * Handles loading state and parallel data fetching
 * 
 * @param {string} partId - The part ID
 * @param {string} revisionId - The revision ID
 * @returns {Object} Document data with loading state and part metadata
 */
const useViewDocumentData = (partId, revisionId) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [partData, setPartData] = useState(null);

  // Select data from Redux store
  const { revisionData, trainingLogs } = useSelector(
    (state) => state.procedureRevision
  );
  const { bomTable } = useSelector((state) => state.parts);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!partId || !revisionId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch all required data in parallel for better performance
        const results = await Promise.all([
          dispatch(getProcedureRevisionsForPart({ partId })),
          dispatch(getTrainingLogForPart({ partId })),
          dispatch(getBom({ partId, revisionId })),
          // Note: getRevAllNodes is called by RenderRevisionNodes component
        ]);

        // Extract current revision data from results
        const revisions = results[0].payload?.results;
        const currentRev = revisions?.find(
          (rev) => rev.procedure_revision_id === revisionId
        );

        // Set part data for display
        if (currentRev) {
          setPartData({
            title: currentRev.part_description,
            partNumber: currentRev.part_number,
            revision: currentRev.rev,
            type: currentRev.type_name,
          });
        }
      } catch (error) {
        console.error("Error fetching revision data:", error);
        setPartData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [partId, revisionId, dispatch]);

  return {
    // Loading state
    loading,
    
    // Data
    partData,
    revisionData,
    trainingLogs,
    bomTable,
  };
};

export default useViewDocumentData;

